import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

function isInsideModalContent(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return target.closest('[data-modal-content]') !== null;
}

export interface AlertModalProps {
  isOpen: boolean;
  title?: string;
  content: string;
  confirmText?: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  onConfirm: () => void;
  autoClose?: boolean;
  autoCloseDelay?: number;
}

export default function AlertModal({
  isOpen,
  title = 'Notification',
  content,
  confirmText = 'OK',
  type = 'info',
  onConfirm,
  autoClose = true,
  autoCloseDelay = 5000,
}: AlertModalProps) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const mouseDownInsideContent = useRef(false);
  const startTimeRef = useRef<number>(0);
  const totalSeconds = Math.ceil(autoCloseDelay / 1000);
  const [countdown, setCountdown] = useState(0);

  // Handle auto-close timer and countdown
  useEffect(() => {
    if (isOpen && autoClose) {
      startTimeRef.current = Date.now();

      // Initialize countdown synchronously using flushSync to avoid cascading renders
      flushSync(() => {
        setCountdown(totalSeconds);
      });

      // Countdown interval (update every second)
      intervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        const remaining = Math.max(1, totalSeconds - elapsed);
        setCountdown(remaining);
      }, 1000);

      // Auto close timer
      timerRef.current = setTimeout(() => {
        onConfirm();
      }, autoCloseDelay);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isOpen, autoClose, autoCloseDelay, onConfirm, totalSeconds]);

  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <div className="flex items-center justify-center w-14 h-14 rounded-full mb-4 bg-green-100 text-green-600">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <path d="m9 11 3 3L22 4" />
            </svg>
          </div>
        );
      case 'error':
        return (
          <div className="flex items-center justify-center w-14 h-14 rounded-full mb-4 bg-red-100 text-red-600">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="m15 9-6 6" />
              <path d="m9 9 6 6" />
            </svg>
          </div>
        );
      case 'warning':
        return (
          <div className="flex items-center justify-center w-14 h-14 rounded-full mb-4 bg-amber-100 text-amber-600">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <path d="M12 9v4" />
              <path d="M12 17h.01" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="flex items-center justify-center w-14 h-14 rounded-full mb-4 bg-blue-100 text-blue-600">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4" />
              <path d="M12 8h.01" />
            </svg>
          </div>
        );
    }
  };

  /* 按钮颜色映射 */
  const buttonColorMap: Record<string, string> = {
    success: 'bg-green-500 hover:bg-green-600',
    error: 'bg-red-500 hover:bg-red-600',
    warning: 'bg-amber-500 hover:bg-amber-600',
    info: 'bg-blue-500 hover:bg-blue-600',
  };

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-5 animate-[modalOverlayFadeIn_0.2s_ease-out]"
      onMouseDown={e => {
        mouseDownInsideContent.current = isInsideModalContent(e.target);
      }}
      onClick={e => {
        if (e.target === overlayRef.current && !mouseDownInsideContent.current) {
          onConfirm();
        }
      }}
    >
      <div
        data-modal-content
        className="bg-white rounded-2xl w-full max-w-[400px] shadow-xl animate-[modalContentSlideIn_0.2s_ease-out]"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="flex flex-col items-center pt-8 px-6 pb-6 text-center">
          {getIcon()}
          <div className="flex flex-col gap-2">
            <h3 className="text-base font-semibold text-gray-900 m-0">{title}</h3>
            <p className="text-sm text-gray-500 leading-normal m-0 whitespace-pre-wrap break-words text-left max-w-full">
              {content}
            </p>
          </div>
        </div>
        <div className="flex justify-center px-6 pb-6">
          <button
            className={`py-3 px-8 h-12 w-full text-base font-semibold rounded-xl border-none cursor-pointer transition-all duration-150 min-w-[100px] tracking-wide shadow-sm text-white hover:opacity-90 hover:-translate-y-px hover:shadow-lg focus:outline-none focus:ring-[3px] focus:ring-blue-500/30 active:translate-y-0 active:shadow-sm ${buttonColorMap[type] || buttonColorMap.info}`}
            onClick={onConfirm}
          >
            {confirmText}
            {autoClose && ` (${countdown})`}
          </button>
        </div>
      </div>
    </div>
  );
}
